import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./skillFoundIn.html";
import { PackageMembersFetcher } from '~/modules/helpers/packageMembersFetchHelper';
import { getCurrentLocalePrefix } from '~/modules/helpers/localeHelper';
import { WireI18n } from '~/modules/stores/i18nStore';
import { getParentPluginListingIds } from '~/modules/utilities/skill_utils';
import { generateListingUrl } from '~/modules/utilities/listing_utils';
const PLUGIN_TYPE_ICON = 'apex_plugin'; // matches wds-listing-card's PLUGIN_BADGE_ICON
const SKILL_TYPE_ICON = 'work_order_type'; // matches wds-listing-card's SKILL_BADGE_ICON

/**
 * Found In tab body: resolves the parent plugins a skill belongs to and renders them through the
 * shared `listing-package-contents-viewer` as a "Plugins" section, alongside a "Skills" roll-up
 * tile (always 0 today — a skill's parents are plugins). Degrades to the viewer's own empty state
 * if the BFF returns [] (soft-failed resolution) — never throws.
 */
class SkillFoundIn extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    // Reactive fields must be initialized (not merely declared) to re-render under the client build.
    this.members = [];
    this.isLoadingMembers = false;
    this.membersFetcher = new PackageMembersFetcher(this, getParentPluginListingIds);
  }
  get listing() {
    return this._listing;
  }

  // Setter is the single fetch trigger: also covers in-place navigation, where a keyless
  // if:true in the parent reassigns this property instead of remounting the component.
  set listing(value) {
    this._listing = value;
    this.membersFetcher.onListingChange(value);
  }
  disconnectedCallback() {
    this.membersFetcher.disconnect();
  }
  get viewerTitle() {
    return this.labels?.foundInViewerTitle ?? '';
  }
  get searchPlaceholder() {
    return this.labels?.foundInSearchPlaceholder ?? '';
  }
  get loadingLabel() {
    return this.labels?.loading ?? '';
  }
  get emptySearchLabel() {
    return this.labels?.emptySearch ?? '';
  }
  get displayListLabel() {
    return this.labels?.displayListLabel ?? '';
  }
  get displayCardsLabel() {
    return this.labels?.displayCardsLabel ?? '';
  }
  get pluginsSectionHeading() {
    return this.labels?.pluginsSectionHeading ?? '';
  }
  get skillsSectionHeading() {
    return this.labels?.skillsSectionHeading ?? '';
  }
  get pluginTypeLabel() {
    return this.labels?.pluginTypeLabel ?? '';
  }
  get skillTypeLabel() {
    return this.labels?.skillTypeLabel ?? '';
  }
  get pluginMembers() {
    return this.members.filter(member => member.technology === 'ai-plugin');
  }
  get skillMembers() {
    return this.members.filter(member => member.technology === 'ai-skill');
  }
  get rollupTiles() {
    return [{
      label: this.pluginsSectionHeading,
      count: this.pluginMembers.length
    }, {
      label: this.skillsSectionHeading,
      count: this.skillMembers.length
    }];
  }
  toPackageItem(member, isPlugin) {
    // Purely relative, never host-qualified: members are same-origin in whichever app the user
    // is browsing, so the link keeps them in the current experience on every domain.
    const url = generateListingUrl('', {
      technology: member.technology
    }, `listingId=${encodeURIComponent(member.id)}`, getCurrentLocalePrefix());
    return {
      id: member.id,
      title: member.name,
      subtitle: member.publisher,
      description: member.description,
      logoUrl: member.logoUrl,
      typeLabel: isPlugin ? this.pluginTypeLabel : this.skillTypeLabel,
      typeIcon: isPlugin ? PLUGIN_TYPE_ICON : SKILL_TYPE_ICON,
      url,
      categories: [],
      // Card view renders this member through the same wds-listing-card the Skills/Plugin
      // landing pages use; the "Plugin" badge is auto-detected from type: 'ai-plugin'.
      cardData: {
        oafId: member.id,
        type: isPlugin ? 'ai-plugin' : 'ai-skill',
        listingUrl: url,
        title: member.name,
        publisher: member.publisher,
        description: member.description,
        image: member.logoUrl,
        pricing: '',
        reviewsAmount: 0,
        averageRating: 0,
        listingCategories: [],
        chatEnabled: false,
        sponsored: false
      }
    };
  }
  get sections() {
    const sections = [];
    if (this.pluginMembers.length > 0) {
      sections.push({
        heading: this.pluginsSectionHeading,
        items: this.pluginMembers.map(member => this.toPackageItem(member, true))
      });
    }
    if (this.skillMembers.length > 0) {
      sections.push({
        heading: this.skillsSectionHeading,
        items: this.skillMembers.map(member => this.toPackageItem(member, false))
      });
    }
    return sections;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SkillFoundIn, {
  publicProps: {
    listing: {
      config: 3
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills.packageContents
        };
      }
    }
  },
  fields: ["members", "isLoadingMembers", "membersFetcher"]
});
const __lwc_component_class_internal = _registerComponent(SkillFoundIn, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;